import { StanModal, StanTooltip } from '@/components';
import IcHelp from '@/assets/icons/ic-help-circle.svg';
import { useBalance } from '@/queries/useBalance';
import { createIconElement, MINIMUM_ALLOWED_BALANCE, SENTRY_FEATURES, stanNotify } from '@/lib';
import { CashOutTimeLineDateRow } from './CashOutTimeLineDateRow';
import { refundOrder } from '@/lib/api/income/api';
import { User } from '@/types';
import { useState } from 'react';
import { useAuthStore } from '@/stores';
interface RefundModalProps {
    isOpen: boolean;
    orderRefundData: {
        refundFeeLabel: string;
        processingFeeAmount: string;
        netRevenue: string;
        grossAmount: string;
    };
    orderAmount: number;
    paymentGateway: string;
    user?: User;
    onClose: () => void;
    onRefund: () => void;
    orderId?: string;
}

export function RefundModal({
    isOpen,
    onClose,
    orderRefundData,
    orderAmount,
    paymentGateway,
    user,
    onRefund,
    orderId,
}: RefundModalProps) {
    const { balance, refetchBalance } = useBalance();
    const [refundLoading, setRefundLoading] = useState(false);
    const token = useAuthStore(state => state.token);
    const isAdminLoggedInAsUser = useAuthStore(state => state.isAdminLoggedInAsUser);
    const netBalanceAfterRefund =
        (balance?.stripe_available?.amount ?? 0) + (balance?.stripe_pending?.amount ?? 0) - orderAmount;

    // TODO: Why is paypal always false?
    const insufficientBalanceForRefund =
        paymentGateway === 'paypal' ? false : netBalanceAfterRefund < MINIMUM_ALLOWED_BALANCE;

    const header = insufficientBalanceForRefund ? 'Insufficient Funds Available' : 'Process a Refund';
    const footerNote =
        paymentGateway === 'paypal' || insufficientBalanceForRefund
            ? undefined
            : 'Refunds will be withdrawn from your Account Balance. Conversion rates may apply.';

    const IconHelp = createIconElement(IcHelp, {
        className: 'size-4 cursor-pointer text-brand-main hover:text-brand-bold',
    });
    return (
        <StanModal
            id='refund-modal'
            isOpen={isOpen}
            onClose={onClose}
            title={header}
            primaryActionLabel='Submit'
            footerNote={footerNote}
            primaryActionLoading={refundLoading}
            onPrimaryAction={() => {
                setRefundLoading(true);
                refundOrder(orderId, token, isAdminLoggedInAsUser(), user?.user_id)
                    .then(() => {
                        stanNotify({
                            type: 'success',
                            title: 'Refund Success',
                            text: 'This transaction has been successfully refunded.',
                            duration: 2000,
                        });
                        onRefund();
                        refetchBalance();
                    })
                    .catch(error => {
                        stanNotify({
                            type: 'error',
                            title: 'Refund Error',
                            text: error?.response?.data?.message || 'Something went wrong. Please contact our support.',
                            duration: 5000,
                            error: {
                                message: '[INCOME] failed-to-refund',
                                options: {
                                    tags: { feature: SENTRY_FEATURES.INCOME },
                                    context: { error },
                                },
                            },
                        });
                    })
                    .finally(() => {
                        setRefundLoading(false);
                        onClose();
                    });
            }}
        >
            {insufficientBalanceForRefund && (
                <div v-if='' className='flex flex-col gap-4'>
                    <div className='text-gray flex w-full content-between items-center'>
                        <span className='text-gray text-sm'>
                            Uh oh! It looks like your account balance is too low to process this refund. Please reach
                            out to
                            <a href='mailto:friends@stanwith.me' className='mailto'>
                                friends@stanwith.me
                            </a>
                            for support.
                        </span>
                    </div>
                </div>
            )}
            <div className='mt-0 flex flex-col gap-4 sm:mt-6'>
                {!insufficientBalanceForRefund && (
                    <div className='flex flex-col gap-5'>
                        <div className='flex flex-col items-center justify-between gap-5 text-center sm:flex-row sm:gap-0 sm:text-left'>
                            <div className='flex flex-col'>
                                <div className='flex flex-row gap-0.5'>
                                    <div className='text-gray text-sm font-bold'>Your Customer Will Receive</div>
                                    <StanTooltip
                                        className='flex items-center'
                                        trigger='click'
                                        content={
                                            <a
                                                href='https://help.stan.store/article/43-limit-quantity-discount-codes'
                                                target='_blank'
                                            >
                                                Learn more
                                            </a>
                                        }
                                        position='top'
                                    >
                                        {IconHelp}
                                    </StanTooltip>
                                </div>

                                <div className='text-gray text-xs italic'>In approximately 5-10 business days</div>
                            </div>

                            <h3 className='font-semibold'>{orderRefundData.grossAmount}</h3>
                        </div>

                        {paymentGateway !== 'paypal' && (
                            <div v-if='' className='flex flex-col gap-5'>
                                <hr className='text-neutral-main w-full' />

                                <div className='text-gray text-sm font-bold'>Refund Breakdown</div>

                                <div className='flex flex-col gap-2'>
                                    <CashOutTimeLineDateRow
                                        amount={orderRefundData.netRevenue}
                                        title='Your Net Revenue'
                                    />
                                </div>

                                <div className='flex flex-col gap-2'>
                                    <CashOutTimeLineDateRow
                                        amount={`+${orderRefundData.processingFeeAmount}`}
                                        title={orderRefundData.refundFeeLabel}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </StanModal>
    );
}
